import { type ReactNode, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Dropdown, type DropdownItem } from '@factorialco/f0-react/dist/experimental'
import { Delete, EyeVisible, Pencil, Files } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import DeleteShiftModal from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteShiftModal'
import EditShiftSidepanel from 'modules/shift_management/components/ShiftManagement/EditShiftSidepanel'
import SeeShiftSidepanel from 'modules/shift_management/components/ShiftManagement/SeeShiftSidepanel'
import type { Shift } from 'modules/shift_management/lib/types'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

interface Props {
  children: ReactNode
  shift: Shift
  readOnly: boolean
  isBackup: boolean
  selected: boolean | undefined
  onCopyToClipboard?: () => void
}

type PanelState = 'none' | 'see' | 'edit' | 'delete'
type ShiftActionId = 'see' | 'edit' | 'copy' | 'delete'

const buildShiftCardActions = ({
  readOnly,
  isBackup,
  canCopy,
  onAction,
}: {
  readOnly: boolean
  isBackup: boolean
  canCopy: boolean
  onAction: (action: ShiftActionId) => void
}): DropdownItem[] => {
  const actions: DropdownItem[] = [
    {
      label: i18n.t('shift_management.shift_menu_options.show_details'),
      icon: EyeVisible,
      onClick: () => onAction('see'),
    },
  ]

  if (!readOnly && !isBackup) {
    actions.push({
      label: i18n.t('shift_management.shift_menu_options.edit'),
      icon: Pencil,
      onClick: () => onAction('edit'),
    })

    if (canCopy) {
      actions.push({
        label: i18n.t('common.copy'),
        icon: Files,
        onClick: () => onAction('copy'),
      })
    }
  }

  if (!readOnly) {
    actions.push({ type: 'separator' })
    actions.push({
      label: i18n.t('shift_management.shift_menu_options.delete'),
      icon: Delete,
      critical: true,
      onClick: () => onAction('delete'),
    })
  }

  return actions
}

const ShiftCardActionsMenu = ({
  children,
  shift,
  readOnly,
  isBackup,
  selected,
  onCopyToClipboard,
}: Props) => {
  const [panel, setPanel] = useState<PanelState>('none')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  useUnsafeEffect(() => {
    if (!selected) {
      setPanel('none')
      return
    }
    setIsDropdownOpen(panel === 'none')
  }, [selected, panel])

  const handleAction = (action: ShiftActionId) => {
    setIsDropdownOpen(false)

    switch (action) {
      case 'see':
        setPanel('see')
        break
      case 'edit':
        setPanel('edit')
        break
      case 'copy':
        onCopyToClipboard?.()
        break
      case 'delete':
        setPanel('delete')
        break
      default:
        break
    }
  }

  const actions = buildShiftCardActions({
    readOnly,
    isBackup,
    canCopy: !!onCopyToClipboard,
    onAction: handleAction,
  })

  const stopSelectionPropagation = (event: MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <>
      <Dropdown
        items={actions}
        align="end"
        open={Boolean(selected && isDropdownOpen)}
        onOpenChange={(open) => {
          setIsDropdownOpen(open)
        }}
      >
        {children}
      </Dropdown>

      {panel === 'see' && (
        <Box onClick={stopSelectionPropagation}>
          <SeeShiftSidepanel
            shift={shift}
            readOnly={readOnly}
            onClose={() => setPanel('none')}
            onOpenEditSidepanel={() => setPanel('edit')}
          />
        </Box>
      )}

      {panel === 'edit' && (
        <Box onClick={stopSelectionPropagation}>
          <EditShiftSidepanel shifts={[shift]} onClose={() => setPanel('none')} />
        </Box>
      )}

      {panel === 'delete' && (
        <DeleteShiftModal shift={shift} isBackup={isBackup} onClose={() => setPanel('none')} />
      )}
    </>
  )
}

export { ShiftCardActionsMenu }
